import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  PALETTE_THEMES,
  DEFAULT_PALETTE_THEME,
  MODES,
  DEFAULT_MODE,
  themes,
  getPaletteTheme,
  setPaletteTheme as persistPaletteToStorage,
  applyPaletteThemeToDom,
  getMode,
  setMode as persistModeToStorage,
  resolveMode,
  applyModeToDom,
} from "@/lib/theme";
import { useIconTheme } from "@homy/ui/icons";
import { useAuth } from "@/features/core/auth/context/AuthContext";
import { Core } from "@/api";

const ThemeContext = createContext({
  theme: DEFAULT_PALETTE_THEME,
  paletteTheme: DEFAULT_PALETTE_THEME,
  currentTheme: DEFAULT_PALETTE_THEME,
  mode: DEFAULT_MODE,
  currentMode: DEFAULT_MODE,
  resolvedTheme: MODES.light,
  setTheme: () => {},
  setPaletteTheme: () => {},
  setMode: () => {},
});

export function ThemeProvider({ children }) {
  const [paletteTheme, setPaletteThemeState] = useState(() => getPaletteTheme());
  const [mode, setModeState] = useState(() => getMode());
  const [resolvedTheme, setResolvedTheme] = useState(() => {
    const { resolvedMode } = resolveMode(getMode());
    return resolvedMode;
  });

  const { user, isAuthenticated } = useAuth();
  const lastSyncedUserThemeRef = useRef(null);

  // Synchronize IconThemeContext with current palette theme
  const { setColorMode } = useIconTheme();

  useEffect(() => {
    if (typeof setColorMode === "function") {
      if (paletteTheme === PALETTE_THEMES.semantic) {
        setColorMode("semantic");
      } else {
        setColorMode("monochrome");
      }
    }
  }, [paletteTheme, setColorMode]);

  // Initial and dynamic DOM application for palette theme
  useEffect(() => {
    applyPaletteThemeToDom(paletteTheme);
  }, [paletteTheme]);

  // Initial and dynamic DOM application for color mode
  useEffect(() => {
    const { resolvedMode } = resolveMode(mode);
    setResolvedTheme(resolvedMode);
    applyModeToDom(resolvedMode);
  }, [mode]);

  // Dynamic listener for OS color scheme switches when mode is "system"
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleMediaChange = (e) => {
      let activeStoredMode = DEFAULT_MODE;
      try {
        activeStoredMode = getMode();
      } catch (_e) {
        activeStoredMode = DEFAULT_MODE;
      }
      if (activeStoredMode === MODES.system) {
        const nextResolved = e.matches ? MODES.dark : MODES.light;
        setResolvedTheme(nextResolved);
        applyModeToDom(nextResolved);
      }
    };

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleMediaChange);
      return () => mediaQuery.removeEventListener("change", handleMediaChange);
    } else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleMediaChange);
      return () => mediaQuery.removeListener(handleMediaChange);
    }
  }, []);

  // Update palette theme function
  const updatePaletteTheme = useCallback(
    (newPalette) => {
      if (!Object.values(PALETTE_THEMES).includes(newPalette)) return;
      setPaletteThemeState(newPalette);
      persistPaletteToStorage(newPalette);
      applyPaletteThemeToDom(newPalette);

      if (isAuthenticated && user) {
        Core.api.updateMyProfile({ theme: newPalette }).catch((err) => {
          console.error("Failed to update profile theme:", err);
        });
      }
    },
    [isAuthenticated, user]
  );

  // Update mode function
  const updateMode = useCallback(
    (newMode) => {
      if (!Object.values(MODES).includes(newMode)) return;
      setModeState(newMode);
      persistModeToStorage(newMode);
      const { resolvedMode } = resolveMode(newMode);
      setResolvedTheme(resolvedMode);
      applyModeToDom(resolvedMode);

      if (isAuthenticated && user) {
        Core.api.updateMyProfile({ theme: newMode }).catch((err) => {
          console.error("Failed to update profile mode:", err);
        });
      }
    },
    [isAuthenticated, user]
  );

  // Overloaded setTheme function for backward compatibility
  const setTheme = useCallback(
    (themeOrMode) => {
      if (Object.values(MODES).includes(themeOrMode)) {
        updateMode(themeOrMode);
      } else if (Object.values(PALETTE_THEMES).includes(themeOrMode)) {
        updatePaletteTheme(themeOrMode);
      }
    },
    [updateMode, updatePaletteTheme]
  );

  // Sync with user profile on login or profile change
  useEffect(() => {
    if (!isAuthenticated || !user) {
      lastSyncedUserThemeRef.current = null;
      return;
    }

    if (user.theme) {
      if (user.theme !== lastSyncedUserThemeRef.current) {
        lastSyncedUserThemeRef.current = user.theme;
        if (Object.values(PALETTE_THEMES).includes(user.theme)) {
          if (user.theme !== paletteTheme) {
            updatePaletteTheme(user.theme);
          }
        } else if (Object.values(MODES).includes(user.theme)) {
          if (user.theme !== mode) {
            updateMode(user.theme);
          }
        }
      }
    } else if (!user.theme && paletteTheme) {
      lastSyncedUserThemeRef.current = paletteTheme;
      Core.api.updateMyProfile({ theme: paletteTheme }).catch((err) => {
        console.error("Failed to sync initial theme to profile:", err);
      });
    }
  }, [isAuthenticated, user, paletteTheme, mode, updatePaletteTheme, updateMode]);

  const value = useMemo(
    () => ({
      theme: paletteTheme,
      paletteTheme,
      currentTheme: paletteTheme,
      mode,
      currentMode: mode,
      resolvedTheme,
      setTheme,
      setPaletteTheme: updatePaletteTheme,
      setMode: updateMode,
    }),
    [paletteTheme, mode, resolvedTheme, setTheme, updatePaletteTheme, updateMode]
  );

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    // Return safe fallback for isolated unit tests that mount components without ThemeProvider
    return {
      theme: DEFAULT_PALETTE_THEME,
      paletteTheme: DEFAULT_PALETTE_THEME,
      currentTheme: DEFAULT_PALETTE_THEME,
      mode: DEFAULT_MODE,
      currentMode: DEFAULT_MODE,
      resolvedTheme: MODES.light,
      setTheme: () => {},
      setPaletteTheme: () => {},
      setMode: () => {},
    };
  }
  return context;
}

export default ThemeContext;

